import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { 
  Download, 
  Filter, 
  BarChart3, 
  TrendingUp,
  Users,
  CheckCircle,
  AlertCircle,
  Calendar,
  BookOpen,
  FileText,
  RefreshCw,
  ChevronDown,
  Layout
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import EXAM_API from '../../api/services/examApi';
import SUBJECT_API from '../../api/services/subjectApi';
import resultApi from '../../api/services/resultApi';
import ConfirmationModal from '../../ui/ConfirmationModal';
import SettingsHeader from '../../ui/SettingsHeader';
import Loader from '../../ui/Loader';

const ResultsDashboard = ({ exam, onBack }) => {
  const { classes: apiClasses } = useData();
  const [dashboardData, setDashboardData] = useState({
    overview: null,
    gradeDistribution: null,
    subjectPerformance: null,
    classComparison: null,
    recentResults: []
  });
  const [loading, setLoading] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [filters, setFilters] = useState({
    class: '',
    subject: '',
    dateRange: ''
  });
  const [availableSubjects, setAvailableSubjects] = useState([]);

  useEffect(() => {
    if (exam?._id || exam?.id) {
      loadDashboardData();
      loadSubjectsForClass();
    }
  }, [exam, filters]);

  const loadSubjectsForClass = async () => {
    if (exam?.unitId) {
      try {
        const response = await SUBJECT_API.getSubjectsByClass(exam.unitId, exam.academicYear);
        setAvailableSubjects(response.data || []);
      } catch (error) {
        setAvailableSubjects([]);
      }
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const response = await EXAM_API.getDetailedExamStats(exam._id || exam.id);
      const data = response.data || {};
      setDashboardData({
        overview: data.usageSummary ? {
          totalStudents: data.usageSummary.totalStudents,
          passedStudents: data.usageSummary.passedStudents || 0,
          failedStudents: data.usageSummary.failedStudents || 0,
          averagePercentage: data.overallPercentage
        } : null,
        gradeDistribution: data.gradeDistribution,
        subjectPerformance: data.subjectBreakdown ? 
          Object.keys(data.subjectBreakdown).reduce((acc, key) => {
             acc[key] = data.subjectBreakdown[key].averageMarks || 0;
             return acc;
          }, {}) : null,
        classComparison: null,
        recentResults: [] 
      });
    } catch (error) {
      toast.error('Failed to load updated statistics.');
    } finally {
      setLoading(false);
    }
  };

  const getClassInfo = () => {
    const classInfo = apiClasses.find(cls => cls._id === exam.unitId);
    return classInfo ? classInfo.unitName : 'All Classes';
  };

  const handleGenerateRanks = async () => {
    try {
      setLoading(true);
      await resultApi.calculateExamSummary(exam._id || exam.id, exam.unitId);
      toast.success('Ranks generated successfully!');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to calculate ranks.');
    } finally {
      setLoading(false);
    }
  };

  const confirmPublishResults = async () => {
    setIsPublishing(true);
    setLoading(true);
     try {
       await resultApi.publishResults(exam._id || exam.id, 'published', exam.unitId);
       toast.success('Results published successfully!');
       setShowPublishModal(false);
       loadDashboardData();
     } catch (error) {
        toast.error('Failed to publish results.');
     } finally {
        setIsPublishing(false);
        setLoading(false);
     }
  };

  const SimplePieChart = ({ data, title }) => {
    const total = Object.values(data).reduce((sum, value) => sum + value, 0);
    const colors = [
      'var(--primary-color, #4f46e5)', 
      'var(--secondary-color, #7c3aed)', 
      '#10b981', 
      '#f59e0b', 
      '#ef4444', 
      '#06b6d4'
    ];
    
    return (
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm transition-all hover:shadow-md">
        <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-6">{title}</h3>
        <div className="flex flex-col md:flex-row items-center justify-around gap-8">
          <div className="relative w-40 h-40">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            {Object.entries(data).map(([key, value], index) => {
              const percentage = (value / total) * 100;
              const radius = 40;
              const circumference = 2 * Math.PI * radius;
              const strokeDashoffset = circumference - (percentage / 100) * circumference;
                return (
                  <circle
                    key={key} cx="50" cy="50" r={radius} fill="none"
                    stroke={colors[index % colors.length]} strokeWidth="8"
                    strokeDasharray={circumference}
                    strokeDashoffset={index === 0 ? strokeDashoffset : 
                        Object.entries(data).slice(0, index).reduce((acc, [, val]) => 
                          acc - (val / total) * circumference, circumference)}
                    strokeLinecap="round" className="transition-all duration-700"
                  />
                );
              })}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 leading-none">{total}</div>
                <div className="text-[9px] font-bold text-gray-400 uppercase tracking-tight mt-1">Total</div>
              </div>
            </div>
          </div>
          <div className="flex-1 w-full space-y-2">
            {Object.entries(data).map(([key, value], index) => (
              <div key={key} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-all">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colors[index % colors.length] }} />
                  <span className="text-xs font-bold text-gray-600">Grade {key}</span>
                </div>
                <span className="text-xs font-bold text-gray-900">{value} <span className="text-[10px] text-gray-400 ml-1">({((value / total) * 100).toFixed(0)}%)</span></span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const SimpleBarChart = ({ data, title }) => {
    const maxValue = Math.max(...Object.values(data), 100);
    return (
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm transition-all hover:shadow-md">
        <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-6">{title}</h3>
        <div className="space-y-5">
          {Object.entries(data).map(([key, value]) => {
            const percentage = (value / maxValue) * 100;
            return (
              <div key={key} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-700">{key}</span>
                  <span className="text-xs font-bold" style={{ color: 'var(--primary-color, #4f46e5)' }}>{value}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${percentage}%`, backgroundColor: 'var(--primary-color, #4f46e5)' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading && !dashboardData.overview) return <Loader />;

  return (
    <div className="max-w-6xl mx-auto space-y-12 mb-12">
      <SettingsHeader
        title="Results & Insights"
        description={`${exam.examName} • ${getClassInfo()}`}
        showBack={true}
        onBack={onBack}
        icon={Layout}
        buttons={[
          { label: "Sync Ranks", icon: RefreshCw, onClick: handleGenerateRanks, variant: "secondary" },
          { label: "Publish", icon: CheckCircle, onClick: () => setShowPublishModal(true), variant: "secondary" },
          { label: "Reports", icon: FileText, onClick: () => {}, variant: "secondary" },
        ]}
      />

      {/* Stats Overview - Clean Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Total Students', value: dashboardData.overview?.totalStudents || 0, icon: Users, color: { color: 'var(--primary-color, #4f46e5)' }, bg: { backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' } },
          { label: 'Average Score', value: dashboardData.overview?.averagePercentage ? `${dashboardData.overview.averagePercentage}%` : '0%', icon: TrendingUp, color: { color: 'var(--secondary-color, #7c3aed)' }, bg: { backgroundColor: 'color-mix(in srgb, var(--secondary-color, #7c3aed) 10%, white)' } },
          { label: 'Scheduled Subjects', value: availableSubjects.length || 0, icon: BookOpen, color: { color: 'rgb(147 51 234)' }, bg: { backgroundColor: 'rgb(250 245 255)' } }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4 group hover:border-gray-200 transition-all">
            <div className="p-3 rounded-xl" style={{ ...stat.bg, ...stat.color }}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-0.5">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters Area */}
      <div className="bg-gray-50/50 p-4 rounded-2xl border border-gray-200/50 flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2 px-3 py-1 bg-white rounded-lg border border-gray-100">
           <Filter className="h-3.5 w-3.5 text-gray-400" />
           <span className="text-[10px] font-bold text-gray-400 uppercase">Filters</span>
        </div>
        <select value={filters.class} onChange={e => setFilters({...filters, class: e.target.value})} className="bg-white border text-xs font-bold px-3 py-2 rounded-xl outline-none input-field">
           <option value="">All Classes</option>
           {apiClasses.map(cls => <option key={cls._id} value={cls._id}>{cls.unitName}</option>)}
        </select>
        <select value={filters.subject} onChange={e => setFilters({...filters, subject: e.target.value})} className="bg-white border text-xs font-bold px-3 py-2 rounded-xl outline-none input-field">
           <option value="">All Subjects</option>
           {availableSubjects.map(s => <option key={s.id} value={s.subjectName}>{s.subjectName}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {dashboardData.gradeDistribution && <SimplePieChart data={dashboardData.gradeDistribution} title="Grade Distribution" />}
        {dashboardData.subjectPerformance && <SimpleBarChart data={dashboardData.subjectPerformance} title="Subject Performance" />}
      </div>

      <ConfirmationModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={confirmPublishResults}
        title="Publish Results"
        message="Finalize and publish results to student portals?"
        confirmText="Confirm & Publish"
        isLoading={isPublishing}
      />
    </div>
  );
};

export default ResultsDashboard;
